
/**
 * Helper function to calculate coefficient of variation
 */
export function calculateVariability(values: number[]): number {
  if (values.length < 2) return 0;
  
  const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
  const variance = values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / values.length;
  const stdDev = Math.sqrt(variance);
  
  return mean === 0 ? 0 : stdDev / Math.abs(mean);
}

export function calculateSeasonality(data: number[], period: number): number[] {
  const seasonality = Array(period).fill(0);
  const counts = Array(period).fill(0);
  
  for (let i = 0; i < data.length; i++) {
    const seasonIndex = i % period;
    seasonality[seasonIndex] += data[i];
    counts[seasonIndex]++;
  }
  
  return seasonality.map((sum, i) => counts[i] > 0 ? sum / counts[i] : 0);
}

export function detectAnomalies(data: number[]): number[] {
  const mean = data.reduce((sum, val) => sum + val, 0) / data.length;
  const stdDev = Math.sqrt(
    data.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / data.length
  );
  
  return data.map((value, index) => {
    const zScore = Math.abs((value - mean) / stdDev);
    return zScore > 2 ? index : -1;
  }).filter(index => index !== -1);
}

export function calculateVolatilityIndex(data: any[]): number {
  if (data.length < 2) return 0;
  
  const returns = data.slice(1).map((value, i) => {
    const current = typeof value === 'object' ? value.value || 0 : value;
    const previous = typeof data[i] === 'object' ? data[i].value || 0 : data[i];
    return previous !== 0 ? (current - previous) / previous : 0;
  });
  
  const mean = returns.reduce((sum, ret) => sum + ret, 0) / returns.length;
  const variance = returns.reduce((sum, ret) => sum + Math.pow(ret - mean, 2), 0) / returns.length;
  
  return Math.sqrt(variance);
}

export function calculateLiquidityRisk(data: any[], assumptions: any): number {
  const currentAssets = assumptions.balanceSheet?.cashProjection || 0;
  const currentLiabilities = assumptions.balanceSheet?.accountsPayableGrowth || 0;
  const quickRatio = currentLiabilities > 0 ? currentAssets / currentLiabilities : 1;
  
  // Inverse relationship: lower ratio = higher risk
  return Math.max(0, Math.min(1, 1 - (quickRatio - 0.5) / 1.5));
}

export function calculateMarketCorrelation(data: any[], marketData: any): number {
  // Simplified correlation calculation
  if (!marketData || data.length < 2) return 0.5;
  
  // In a real implementation, this would calculate correlation with market indices
  return Math.random() * 0.4 + 0.3; // Random value between 0.3 and 0.7
}

export function calculatePerformanceRisk(data: any[]): number {
  if (data.length === 0) return 0.5;
  
  const values = data.map(d => typeof d === 'object' ? d.value || 0 : d);
  const negativeCount = values.filter(v => v < 0).length;
  
  return negativeCount / values.length;
}

export function calculateEconomicRisk(assumptions: any): number {
  const inflationRate = assumptions.budget?.inflationRate || 3;
  const growthRate = assumptions.profitLoss?.revenueGrowthRate || 10;
  
  // Higher inflation relative to growth = higher risk
  return Math.max(0, Math.min(1, (inflationRate - growthRate/2) / 10));
}

export function calculateLocalVolatility(data: any[], index: number, window: number): number {
  const start = Math.max(0, index - window);
  const end = Math.min(data.length, index + window + 1);
  const subset = data.slice(start, end);
  
  if (subset.length < 2) return 0;
  
  const values = subset.map(d => typeof d === 'object' ? d.value || 0 : d);
  const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
  const variance = values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / values.length;
  
  return Math.sqrt(variance);
}

export function detectCyclicalPattern(data: number[]): boolean {
  // Simple autocorrelation check for periodicity
  const period = 12; // Monthly cycle
  if (data.length < period * 2) return false;
  
  let correlation = 0;
  const n = data.length - period;
  
  for (let i = 0; i < n; i++) {
    correlation += data[i] * data[i + period];
  }
  
  correlation /= n;
  const mean = data.reduce((sum, val) => sum + val, 0) / data.length;
  const variance = data.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / data.length;
  
  return Math.abs(correlation) > variance * 0.5;
}
